import { BaseMenu, BaseMenuItem, BaseMenuTitle } from '@components/BaseMenu';
import { getMediaActionIcon } from '@components/MediaActionBar/mediaActionIcons';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { isMediaVerbId } from '@lib/verbs';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton } from '@mui/material';
import { trackingStore } from '@stores/Tracking';
import { Media } from '@typings/Media';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { MediaRenameModal } from '../../MediaRenameModal/MediaRenameModal';
import { MobileMediaMenuDrawer } from '../../Mobile/MobileMediaMenuDrawer';
import { useMediaMenuActions } from './useMediaMenuActions';
import css from './VideoMenuButton.module.scss';

interface VideoMenuButtonProps {
  media: Media;
  isModifiable: boolean;
  /**
   * Optional. Fires once when any of the menu, drawer, delete modal,
   * or rename modal transitions from closed to open. VideoCard uses
   * it to pause playback so the video doesn't keep playing behind
   * the overlay. Works for both mouse- and keyboard-triggered opens
   * (doesn't depend on cursor geometry).
   */
  onMenuOpened?: () => void;
  /**
   * Optional. Fires after a rename or delete modal closes.
   * VideoCard uses it to collapse the card's hover overlays so they
   * don't linger on a card the user just finished editing. Other
   * call sites (table rows, search modal) can omit it.
   */
  onAfterDestructiveAction?: () => void;
  iconVariant?: 'horizontal' | 'vertical';
  customIcon?: React.ReactNode;
  /**
   * Reads the host's current playhead position in seconds. Supplying it is what adds the
   * "Copy link at 1:30" row to this menu — see `useMediaMenuActions`' `playheadTime`.
   *
   * Called once, when the menu or drawer opens, and the value is held for as long as it stays
   * open; the hook needs a frozen number, not a moving one. Only a host with a video player
   * has anything to pass — the grid card, the table row and the mobile list row all show a
   * thumbnail and correctly omit it.
   */
  getCurrentTime?: () => number | undefined;
  /**
   * Whether a `MediaVerbRail` is on screen beside this button on MOBILE. When it is, the mobile
   * drawer drops the three verbs rather than repeating what the user can already see.
   *
   * Only the grid card qualifies (`VideoCard` reveals its rail at the centre line on touch).
   * The mobile list row has no rail, so it must keep the verbs and this stays `false`. (The
   * moment preview used to be the other caller; it now mounts `MediaActionBar` instead, which
   * does its own grouping.)
   *
   * Defaults to `false` deliberately: a host that forgets it shows the verbs twice, which is
   * untidy, where the opposite default would strand them entirely. Same trade, same reasoning,
   * as `hasVerbRail` on `MobileLibraryContextDrawer`.
   *
   * Desktop needs no equivalent — that menu omits the verbs from its JSX unconditionally.
   */
  hasVerbRail?: boolean;
}

export const VideoMenuButton = ({
  media,
  isModifiable,
  onMenuOpened,
  onAfterDestructiveAction,
  iconVariant = 'horizontal',
  customIcon,
  getCurrentTime,
  hasVerbRail,
}: VideoMenuButtonProps) => {
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const open = Boolean(anchorEl);

  /**
   * The playhead, sampled when this menu opened.
   *
   * Deliberately never cleared on close. Clearing it would drop the timestamped row mid
   * fade-out, so the last thing the user sees is a menu item vanishing from under the click
   * they just made. A stale value costs nothing: `handleClick` overwrites it before the menu
   * reopens, in the same render as `anchorEl`, and while closed nothing reads it.
   */
  const [playheadTime, setPlayheadTime] = useState<number | undefined>(undefined);

  const { actions, executeAction, mediaRenameModalRef, isRenameModalOpen, setIsRenameModalOpen } =
    useMediaMenuActions(media, isModifiable, onAfterDestructiveAction, playheadTime);

  // Everything except the verbs — see the note in the menu below for why they're absent. The
  // hook has already applied every gate (external origin, errored status, modifiability) and
  // every label, including the timestamp on "Copy link at 1:30", so nothing is decided here.
  const desktopActions = actions.filter((action) => !isMediaVerbId(action.id));

  // Fire `onMenuOpened` once on each closed→open transition across the
  // "something is overlaying the card" states. VideoCard wires this to
  // pauseVideo() so the video doesn't keep playing behind an overlay.
  // Delete modal state isn't tracked here — it's owned by the global
  // MediaDeleteModalContext; by the time it opens, the menu/drawer that
  // launched it was already open, so the pause has already fired.
  const wasAnyOpenRef = useRef(false);
  useEffect(() => {
    const isAnyOpen = open || drawerOpen || isRenameModalOpen;
    if (isAnyOpen && !wasAnyOpenRef.current) {
      onMenuOpened?.();
    }
    wasAnyOpenRef.current = isAnyOpen;
  }, [open, drawerOpen, isRenameModalOpen, onMenuOpened]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    // Before opening, so the frozen playhead is in place for the render that shows the menu.
    setPlayheadTime(getCurrentTime?.());
    if (isMobile) {
      setDrawerOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }
    trackingStore.mediaMenuOpened({ mediaId: media._id, libraryId: media.libraryId });
  };

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    trackingStore.mediaMenuClosed({ mediaId: media._id, libraryId: media.libraryId });
  };

  const handleDesktopAction = (handler: () => void) => (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    handler();
  };

  return (
    <>
      <IconButton
        className={css.threeDots}
        onClick={handleClick}
        aria-label="media menu"
        aria-controls={open ? 'media-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {customIcon ?? (iconVariant === 'vertical' ? <MoreVertIcon /> : <MoreHorizIcon />)}
      </IconButton>

      {isMobile ? (
        <MobileMediaMenuDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            trackingStore.mediaMenuClosed({ mediaId: media._id, libraryId: media.libraryId });
          }}
          onOpen={() => setDrawerOpen(true)}
          media={media}
          // One group: this drawer's hosts show a flat list. (The moment modal's bar passes two
          // and gets a hairline between them.)
          //
          // Filtered here rather than in `actions` itself, which the desktop menu and the
          // table also read — see `hasVerbRail`.
          groups={[hasVerbRail ? actions.filter((action) => !isMediaVerbId(action.id)) : actions]}
          onAction={(action) => executeAction(action, 'media-drawer')}
        />
      ) : (
        <BaseMenu id="media-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
          <BaseMenuTitle>Media settings</BaseMenuTitle>
          {/* The three verbs — Chat with an Agent, Find moments, Clip this media — are NOT
              here. Both remaining desktop surfaces that mount this menu show them on the
              surface itself: the grid card's hover rail and the table row's title-cell rail.
              Repeating them made the menu twice as long as its own "Media settings" title
              claims, which is what this menu is actually for. Same rule `LibraryMenuButton`
              follows on the library side. (The moment preview no longer mounts this component
              at all — it uses `MediaActionBar`, whose menu DOES carry the verbs, under a
              divider instead of a title, because it has no rail beside it.)
              The mobile drawer decides per host via `hasVerbRail`, so `actions` stays
              unfiltered — the table's `MenuCell` and the mobile list both feed the same array
              to paths that still need rename/delete/download.

              Rendered from `actions` rather than as hand-written rows. It used to be ~50 lines
              of conditional JSX duplicating the hook's own gates (`origin.source === 'external'`,
              `status === 'errored'`, `!isModifiable`) and its own icon table — which is exactly
              the desktop/mobile drift `context-menu-actions.md` exists to prevent, and it bit
              immediately: adding one debug-only row and swapping Copy link's glyph would have
              landed in the drawer and not here. `actions` already arrives in `MEDIA_ACTION_ORDER`,
              so the map reproduces the previous order exactly. */}
          {desktopActions.map((item) => (
            <BaseMenuItem
              key={item.id}
              icon={getMediaActionIcon(item.id, media)}
              onClick={handleDesktopAction(() => executeAction(item.id))}
              disabled={item.disabled}
              danger={item.danger}
            >
              {item.label}
            </BaseMenuItem>
          ))}
        </BaseMenu>
      )}

      <MediaRenameModal
        ref={mediaRenameModalRef}
        media={media}
        onOpen={() => setIsRenameModalOpen(true)}
        onClose={() => {
          setIsRenameModalOpen(false);
          onAfterDestructiveAction?.();
        }}
      />
    </>
  );
};
